import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import axios from 'axios';
import {
    TrendingUp, ChevronRight, CheckCircle, CheckCircle2, XCircle, MinusCircle, Radio,
    GraduationCap, Send, MessageCircle, BarChart3, Trophy, Calculator,
    Bot, Sparkles, ShieldCheck, Users, Star,
} from 'lucide-react';
import { CONTACTS, MOTTO, getFreeTelegramUrl, getWhatsappGroupUrl } from '../config/brand';
import { SocialLinks } from '../components/SocialLinks';
import { ComplianceLinks, EduDisclaimer } from '../components/PublicShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSETS = {
    before: 'https://customer-assets-lqy194kg.emergentagent.net/job_forex-trading-portal-1/artifacts/kujldsy8_photo_5987814448426585642_y%20%281%29.jpg',
    after: 'https://customer-assets-lqy194kg.emergentagent.net/job_forex-trading-portal-1/artifacts/uoxdm0ch_photo_5987814448426585643_w%20%281%29.jpg',
    crtChart: 'https://customer-assets-lqy194kg.emergentagent.net/job_forex-trading-portal-1/artifacts/qcljgo63_GBPAUD_2026-07-24_19-10-34.png',
    mobileGold: 'https://customer-assets-lqy194kg.emergentagent.net/job_forex-trading-portal-1/artifacts/qu1vtybl_photo_6005970310273895708_w.webp',
};

const SIGNAL_STATUS_META = {
    tp_hit: { key: 'tp', color: '#10B981', Icon: CheckCircle2 },
    sl_hit: { key: 'sl', color: '#EF4444', Icon: XCircle },
    be: { key: 'be', color: '#A1A1AA', Icon: MinusCircle },
};

const TIERS = [
    { key: 'starter', label: 'STARTER', focus: 'Rulebook basics', color: '#CD7F32' },
    { key: 'builder', label: 'BUILDER', focus: 'Journal discipline', color: '#B8C4CE' },
    { key: 'elite', label: 'ELITE', focus: 'Full CRT application', color: '#FFB800' },
];

export default function LandingPage() {
    const { t, i18n } = useTranslation();
    const [recentSignals, setRecentSignals] = useState([]);
    const [challengeWinners, setChallengeWinners] = useState([]);
    const [reviews, setReviews] = useState([]);

    useEffect(() => {
        axios.get(`${API}/public/signals/recent?limit=10`)
            .then((r) => setRecentSignals(r.data))
            .catch(() => {});
        axios.get(`${API}/public/challenge/winners`)
            .then((r) => setChallengeWinners(r.data))
            .catch(() => {});
        axios.get(`${API}/public/reviews?limit=9`)
            .then((r) => setReviews(r.data))
            .catch(() => {});
    }, []);

    const closed = recentSignals.filter((s) => ['tp_hit', 'sl_hit', 'be'].includes(s.status));
    const wins = closed.filter((s) => s.status === 'tp_hit').length;
    const winRate = closed.length > 0 ? Math.round((wins / closed.length) * 100) : 0;
    const totalR = closed.reduce((acc, s) => acc + (typeof s.rr_ratio === 'number' ? (s.status === 'tp_hit' ? s.rr_ratio : s.status === 'sl_hit' ? -1 : 0) : 0), 0);

    return (
        <div className="min-h-screen bg-[#050505]">
            <Header gold />

            {/* ============ HERO — BLACK ============ */}
            <section className="relative overflow-hidden bg-[#050505] text-white" data-testid="hero-section">
                <div className="absolute inset-0 opacity-[0.16]" style={{ backgroundImage: 'radial-gradient(ellipse 70% 50% at 50% -10%, rgba(212,175,55,0.55), transparent 65%)' }} />
                <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)', backgroundSize: '56px 56px' }} />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
                    <div className="grid lg:grid-cols-12 gap-14 items-center">
                        <div className="lg:col-span-7">
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 mb-8" data-testid="hero-badge">
                                <Sparkles className="w-3.5 h-3.5 text-[#FFB800]" />
                                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#FFB800]">{t('landing.hero2.trustedTools')}</span>
                            </div>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                                {t('landing.hero2.line1')}
                                <br />
                                <span className="text-[#FFB800]" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontWeight: 600 }}>
                                    {t('landing.hero2.line2')}
                                </span>
                            </h1>
                            <p className="text-base lg:text-lg text-zinc-400 leading-relaxed max-w-xl mb-9">
                                {t('landing.heroDescription')}
                            </p>
                            <div className="flex flex-wrap items-center gap-4">
                                <Link to="/register">
                                    <Button className="h-13 px-8 py-6 rounded-full bg-white text-black font-bold text-base hover:bg-zinc-200 hover:scale-[1.02] transition-transform" data-testid="hero-cta-register">
                                        {t('landing.startJourney')}
                                        <ChevronRight className="w-5 h-5 ms-1" />
                                    </Button>
                                </Link>
                                <Link to="/login" className="text-sm font-semibold text-zinc-300 hover:text-white underline underline-offset-4 decoration-zinc-600" data-testid="hero-cta-login">
                                    {t('landing.continueLearning')}
                                </Link>
                            </div>

                            {/* stats strip */}
                            <div className="mt-14 grid grid-cols-3 max-w-md divide-x divide-zinc-800 border border-zinc-800 rounded-2xl overflow-hidden" data-testid="hero-stats">
                                <div className="px-5 py-4 bg-zinc-950">
                                    <p className="text-2xl font-black font-mono text-[#FFB800]">{closed.length > 0 ? `${winRate}%` : 'LIVE'}</p>
                                    <p className="text-[11px] uppercase tracking-widest text-zinc-500">{t('landing.stats.winRate')}</p>
                                </div>
                                <div className="px-5 py-4 bg-zinc-950">
                                    <p className="text-2xl font-black font-mono text-white">24/7</p>
                                    <p className="text-[11px] uppercase tracking-widest text-zinc-500">{t('landing.stats.access')}</p>
                                </div>
                                <div className="px-5 py-4 bg-zinc-950">
                                    <p className="text-2xl font-black font-mono text-white">3</p>
                                    <p className="text-[11px] uppercase tracking-widest text-zinc-500">{t('landing.stats.phases')}</p>
                                </div>
                            </div>
                        </div>

                        {/* Before / After proof polaroids */}
                        <div className="lg:col-span-5 relative hidden md:block" data-testid="hero-proof">
                            <div className="relative h-[460px]">
                                <div className="absolute top-0 start-0 w-[78%] rotate-[-4deg] rounded-xl overflow-hidden border border-zinc-700 bg-zinc-900 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
                                    <div className="px-3 py-2 flex items-center justify-between bg-zinc-950">
                                        <span className="text-[10px] font-bold tracking-[0.25em] text-zinc-400">{t('landing.hero2.beforeLabel')}</span>
                                        <span className="w-2 h-2 rounded-full bg-[#FFB800]" />
                                    </div>
                                    <img src={ASSETS.before} alt="Trade prediction" className="w-full h-48 object-cover" loading="lazy" />
                                </div>
                                <div className="absolute bottom-6 end-0 w-[78%] rotate-[3deg] rounded-xl overflow-hidden border border-[#D4AF37]/60 bg-zinc-900 shadow-[0_0_50px_rgba(255,184,0,0.18)]">
                                    <div className="px-3 py-2 flex items-center justify-between bg-zinc-950">
                                        <span className="text-[10px] font-bold tracking-[0.25em] text-[#FFB800]">{t('landing.hero2.afterLabel')}</span>
                                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                    </div>
                                    <img src={ASSETS.after} alt="Trade result" className="w-full h-48 object-cover" loading="lazy" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ FEATURES BENTO — WHITE ============ */}
            <section className="bg-white text-zinc-900 py-24 lg:py-32" data-testid="features-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mb-16">
                        <p className="text-xs tracking-[0.25em] uppercase font-bold mb-4 text-[#B8860B]">{t('landing.features.overline')}</p>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            {t('landing.features.title')}{' '}
                            <span className="text-[#B8860B]" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontWeight: 600 }}>{t('landing.features.titleHighlight')}</span>
                        </h2>
                        <p className="text-base text-zinc-600 leading-relaxed">{t('landing.features.description')}</p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                        {/* CRT Engine — wide card with chart in window frame */}
                        <div className="lg:col-span-7 rounded-2xl border border-zinc-200 bg-zinc-50 p-7 hover:border-[#D4AF37] transition-colors group" data-testid="feature-engine">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-xl bg-[#050505] flex items-center justify-center">
                                    <Radio className="w-5 h-5 text-[#FFB800]" />
                                </div>
                                <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('landing.features.engine.title')}</h3>
                            </div>
                            <p className="text-sm text-zinc-600 mb-6 leading-relaxed">{t('landing.features.engine.desc')}</p>
                            <div className="rounded-xl overflow-hidden border border-zinc-300 shadow-sm group-hover:-translate-y-1 transition-transform">
                                <div className="flex items-center gap-1.5 px-3 py-2 bg-zinc-100 border-b border-zinc-200">
                                    <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                                    <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                                    <span className="ms-3 text-[11px] font-mono text-zinc-500">GBPAUD · 1H · CRT study example</span>
                                </div>
                                <img src={ASSETS.crtChart} alt="AI generated CRT analysis chart" className="w-full object-cover" loading="lazy" />
                            </div>
                        </div>

                        <div className="lg:col-span-5 flex flex-col gap-5">
                            {/* Risk Calculator mock */}
                            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-7 hover:border-[#D4AF37] transition-colors" data-testid="feature-risk-calc">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-xl bg-[#050505] flex items-center justify-center">
                                        <Calculator className="w-5 h-5 text-[#FFB800]" />
                                    </div>
                                    <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('landing.features.riskCalc.title')}</h3>
                                </div>
                                <p className="text-sm text-zinc-600 mb-5 leading-relaxed">{t('landing.features.riskCalc.desc')}</p>
                                <div className="rounded-xl border border-zinc-200 bg-white p-4 space-y-3">
                                    <div>
                                        <div className="flex justify-between text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                                            <span>{t('landing.features.riskCalc.capital')}</span><span className="font-mono text-zinc-900 font-bold">1,000</span>
                                        </div>
                                        <div className="h-1.5 rounded-full bg-zinc-200"><div className="h-full w-2/3 rounded-full bg-[#050505]" /></div>
                                    </div>
                                    <div>
                                        <div className="flex justify-between text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                                            <span>{t('landing.features.riskCalc.riskLabel')}</span><span className="font-mono text-zinc-900 font-bold">2%</span>
                                        </div>
                                        <div className="h-1.5 rounded-full bg-zinc-200"><div className="h-full w-1/4 rounded-full bg-[#D4AF37]" /></div>
                                    </div>
                                    <div className="flex items-end justify-between pt-2 border-t border-zinc-100">
                                        <div>
                                            <p className="text-[11px] uppercase tracking-wider text-zinc-500">{t('landing.features.riskCalc.lotSize')}</p>
                                            <p className="font-mono font-black text-lg">0.12</p>
                                        </div>
                                        <div className="text-end">
                                            <p className="text-[11px] uppercase tracking-wider text-zinc-500">{t('landing.features.riskCalc.potential')}</p>
                                            <p className="font-mono font-black text-lg text-emerald-600">2.0R</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* AI Assistant chat mock */}
                            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-7 hover:border-[#D4AF37] transition-colors flex-1" data-testid="feature-assistant">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-xl bg-[#050505] flex items-center justify-center">
                                        <Bot className="w-5 h-5 text-[#FFB800]" />
                                    </div>
                                    <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('landing.features.assistant.title')}</h3>
                                </div>
                                <p className="text-sm text-zinc-600 mb-5 leading-relaxed">{t('landing.features.assistant.desc')}</p>
                                <div className="space-y-2.5">
                                    <div className="max-w-[85%] ms-auto rounded-2xl rounded-ee-sm bg-[#050505] text-white text-sm px-4 py-2.5">
                                        {t('landing.features.assistant.chatQ')}
                                    </div>
                                    <div className="max-w-[90%] rounded-2xl rounded-ss-sm bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-zinc-800 text-sm px-4 py-2.5">
                                        {t('landing.features.assistant.chatA')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ PILLARS — LIGHT ZINC ============ */}
            <section className="bg-zinc-50 text-zinc-900 py-24 border-t border-zinc-200" data-testid="pillars-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-14">
                        <p className="text-xs tracking-[0.25em] uppercase font-bold mb-4 text-[#B8860B]">{t('landing.pillars.overline')}</p>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            {t('landing.pillars.title')}{' '}
                            <span className="text-[#B8860B]" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontWeight: 600 }}>{t('landing.pillars.titleHighlight')}</span>
                        </h2>
                        <p className="text-base max-w-2xl mx-auto text-zinc-600">{t('landing.pillars.description')}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {[
                            { icon: GraduationCap, key: 'education' },
                            { icon: BarChart3, key: 'signals' },
                            { icon: Send, key: 'telegram' },
                        ].map(({ icon: Icon, key }) => (
                            <div key={key} className="bg-white rounded-2xl border border-zinc-200 p-7 hover:border-[#D4AF37] hover:-translate-y-1 transition-[border-color,transform]" data-testid={`pillar-${key}`}>
                                <div className="w-11 h-11 rounded-xl bg-[#050505] flex items-center justify-center mb-5">
                                    <Icon className="w-5 h-5 text-[#FFB800]" />
                                </div>
                                <h3 className="text-lg font-bold mb-2 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                                    {t(`landing.pillars.${key}.title`)}
                                </h3>
                                <p className="text-sm leading-relaxed mb-4 text-zinc-600">{t(`landing.pillars.${key}.description`)}</p>
                                <ul className="space-y-2 text-sm">
                                    {(t(`landing.pillars.${key}.items`, { returnObjects: true }) || []).map((x) => (
                                        <li key={x} className="flex items-center gap-2 text-zinc-700">
                                            <CheckCircle className="w-4 h-4 flex-shrink-0 text-[#B8860B]" />
                                            {x}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ LIVE TRACK RECORD — DARK ============ */}
            <section className="bg-zinc-950 text-white py-24 lg:py-32" data-testid="signals-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
                        <div>
                            <div className="inline-flex items-center gap-2 mb-4">
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                                </span>
                                <span className="text-xs font-semibold tracking-[0.25em] uppercase text-emerald-400">{t('landing.track.badge')}</span>
                            </div>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                                {t('landing.track.title')}
                            </h2>
                            <p className="text-base max-w-xl text-zinc-400">{t('landing.track.description')}</p>
                        </div>
                        {closed.length > 0 && (
                            <div className="flex items-center gap-10">
                                <div>
                                    <p className="text-4xl font-black font-mono text-emerald-400">{winRate}%</p>
                                    <p className="text-[11px] uppercase tracking-widest text-zinc-500">{t('landing.track.winRate')}</p>
                                </div>
                                <div>
                                    <p className={`text-4xl font-black font-mono ${totalR >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {totalR >= 0 ? '+' : ''}{totalR.toFixed(1)}R
                                    </p>
                                    <p className="text-[11px] uppercase tracking-widest text-zinc-500">{t('landing.track.totalR')}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {closed.length > 0 && (
                        <div className="border border-zinc-800 rounded-2xl overflow-hidden divide-y divide-zinc-800" data-testid="signals-list">
                            {closed.slice(0, 6).map((s) => {
                                const meta = SIGNAL_STATUS_META[s.status];
                                return (
                                    <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 bg-zinc-950 hover:bg-zinc-900 transition-colors" data-testid={`signal-row-${s.id}`}>
                                        <div className="flex items-center gap-4">
                                            <span className={`font-black font-mono text-sm ${s.direction === 'BUY' ? 'text-emerald-400' : 'text-red-400'}`}>{s.direction}</span>
                                            <span className="font-bold tracking-tight">{s.market}</span>
                                        </div>
                                        <div className="hidden sm:block font-mono text-xs text-zinc-500">
                                            {s.closed_at ? new Date(s.closed_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {typeof s.rr_ratio === 'number' && s.status === 'tp_hit' && (
                                                <span className="font-mono font-bold text-emerald-400 text-sm">+{s.rr_ratio.toFixed(2)}R</span>
                                            )}
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase border" style={{ color: meta.color, borderColor: `${meta.color}55`, backgroundColor: `${meta.color}14` }}>
                                                <meta.Icon className="w-3 h-3" />
                                                {t(`landing.track.status.${meta.key}`)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <p className="mt-6 text-center text-[11px] text-zinc-500" data-testid="track-disclaimer">Educational journal only. Not financial advice. Past results are not indicative of future outcomes.</p>
                    <div className="mt-6 text-center">
                        <Link to="/register">
                            <Button className="rounded-full px-7 py-5 bg-transparent border border-zinc-700 text-white hover:border-[#FFB800] hover:text-[#FFB800] transition-colors" data-testid="signals-cta-join">
                                <Radio className="w-4 h-4 me-2" />
                                {t('landing.track.join')}
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* ============ 31-DAY CHALLENGE — BLACK/GOLD ============ */}
            <section className="bg-[#050505] text-white py-24 lg:py-32 border-t border-zinc-900 relative overflow-hidden" data-testid="challenge-section">
                <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: 'radial-gradient(ellipse 60% 45% at 50% 0%, rgba(255,184,0,0.7), transparent 65%)' }} />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-14">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#FFB800]/40 bg-[#FFB800]/10 mb-5">
                            <Trophy className="w-3.5 h-3.5 text-[#FFB800]" />
                            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#FFB800]">{t('challenge.badge')}</span>
                        </div>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            {t('challenge.landingTitle')}{' '}
                            <span className="text-[#FFB800]" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontWeight: 600 }}>{t('challenge.landingTitleHighlight')}</span>
                        </h2>
                        <p className="text-base max-w-2xl mx-auto text-zinc-400">{t('challenge.landingDescription')}</p>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12">
                        {TIERS.map((tier) => (
                            <div key={tier.label} className="rounded-2xl p-5 text-center bg-zinc-950 border transition-transform hover:-translate-y-1" style={{ borderColor: `${tier.color}45`, boxShadow: `0 0 34px ${tier.color}12` }} data-testid={`landing-tier-${tier.key}`}>
                                <p className="text-xs font-black tracking-[0.2em] uppercase mb-3" style={{ color: tier.color }}>{tier.label}</p>
                                <p className="text-sm font-bold text-zinc-300">{tier.focus}</p>
                                <p className="text-[10px] uppercase tracking-widest text-zinc-600 mt-2">31 days · journal · certificate</p>
                            </div>
                        ))}
                    </div>

                    {challengeWinners.length > 0 && (
                        <div className="mb-12" data-testid="challenge-winners-showcase">
                            <p className="text-center text-xs uppercase tracking-[0.25em] font-bold mb-5 text-[#FFB800]">{t('challenge.winnersShowcase')}</p>
                            <div className="flex flex-wrap justify-center gap-3">
                                {challengeWinners.map((w, i) => (
                                    <div key={i} className="rounded-full ps-2 pe-5 py-2 flex items-center gap-3 bg-zinc-950 border border-[#FFB800]/35">
                                        <span className="w-9 h-9 rounded-full bg-[#FFB800]/15 flex items-center justify-center">
                                            <Trophy className="w-4 h-4 text-[#FFB800]" />
                                        </span>
                                        <div>
                                            <p className="text-sm font-bold leading-tight">{w.name}</p>
                                            <p className="text-[11px] text-[#FFB800] leading-tight">{w.tier_label} · Challenge completed</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="text-center">
                        <Link to="/register">
                            <Button className="rounded-full px-9 py-6 h-auto bg-[#FFB800] text-black font-black text-base hover:bg-[#FFC933] hover:scale-[1.02] transition-transform" data-testid="challenge-cta-join">
                                <Trophy className="w-5 h-5 me-2" />
                                {t('challenge.landingCta')}
                            </Button>
                        </Link>
                        <p className="text-xs mt-4 text-zinc-500">🎁 {t('challenge.prizeNote')}</p>
                    </div>
                </div>
            </section>

            {/* ============ TESTIMONIALS ============ */}
            {reviews.length > 0 && (
                <section className="bg-zinc-950 text-white py-24 border-t border-zinc-900" data-testid="testimonials-section">
                    <div className="max-w-6xl mx-auto px-4">
                        <p className="text-xs font-black tracking-[0.3em] uppercase text-[#FFB800] mb-3 text-center">{t('landing.reviews.label')}</p>
                        <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-3 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            {t('landing.reviews.title')}
                        </h2>
                        <p className="text-base text-zinc-400 mb-12 text-center max-w-2xl mx-auto">{t('landing.reviews.subtitle')}</p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {reviews.slice(0, 9).map((r) => (
                                <div key={r.id} className="rounded-2xl p-6 bg-zinc-900/60 border border-zinc-800 hover:border-[#FFB800]/40 transition-colors" data-testid={`testimonial-${r.id}`}>
                                    <div className="flex items-center gap-1 mb-3">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star key={n} className="w-4 h-4 text-[#FFB800]" fill={r.rating >= n ? '#FFB800' : 'transparent'} />
                                        ))}
                                    </div>
                                    <p className="text-sm text-zinc-300 leading-relaxed mb-4">“{r.text}”</p>
                                    <p className="text-sm font-bold flex items-center gap-2">
                                        {r.name}
                                        {r.country && <span className="px-1.5 py-0.5 rounded text-[10px] font-black bg-[#FFB800]/10 text-[#FFB800] border border-[#FFB800]/30">{r.country}</span>}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ============ FINAL CTA ============ */}
            <section className="bg-[#050505] text-white py-24 border-t border-zinc-900 text-center" data-testid="final-cta-section">
                <div className="max-w-3xl mx-auto px-4">
                    <ShieldCheck className="w-10 h-10 mx-auto mb-6 text-[#FFB800]" />
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {t('landing.cta.title')}
                    </h2>
                    <p className="text-base text-zinc-400 mb-9">{t('landing.cta.description')}</p>
                    <Link to="/register">
                        <Button className="rounded-full px-10 py-6 h-auto bg-white text-black font-black text-base hover:bg-zinc-200 hover:scale-[1.02] transition-transform" data-testid="final-cta-register">
                            {t('landing.startJourney')}
                            <ChevronRight className="w-5 h-5 ms-1" />
                        </Button>
                    </Link>
                </div>
            </section>

            {/* ============ FOOTER — STARK BLACK ============ */}
            <footer className="py-12 bg-[#050505] border-t border-zinc-900" data-testid="landing-footer">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-center gap-2 mb-6">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#FFB800]">
                            <TrendingUp className="w-4 h-4 text-black" />
                        </div>
                        <span className="font-black tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>NIKAYO FX GUIDE</span>
                    </div>
                    <p className="text-center mb-6 text-sm sm:text-base italic font-semibold tracking-[0.15em]" style={{ fontFamily: "'Playfair Display', serif", color: '#FFB800' }} data-testid="brand-motto">
                        “{MOTTO}”
                    </p>
                    <div className="flex flex-wrap justify-center gap-3 mb-6">
                        <a href={getFreeTelegramUrl(i18n.language)} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm bg-[#229ED9] text-white hover:scale-[1.03] transition-transform"
                           data-testid="footer-free-telegram-link">
                            <Send className="w-4 h-4" />
                            {t('landing.footer.freeTelegram')}
                        </a>
                        <a href={CONTACTS.whatsappCta.url} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm bg-[#25D366] text-white hover:scale-[1.03] transition-transform"
                           data-testid="footer-whatsapp-cta">
                            <MessageCircle className="w-4 h-4" />
                            {t('landing.footer.whatsapp')}
                        </a>
                        <a href={getWhatsappGroupUrl(i18n.language)} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm bg-[#128C7E] text-white hover:scale-[1.03] transition-transform"
                           data-testid="footer-whatsapp-group-link">
                            <Users className="w-4 h-4" />
                            {t('landing.footer.whatsappGroup')}
                        </a>
                    </div>
                    <div className="max-w-3xl mx-auto space-y-2 mb-6 text-center" data-testid="footer-disclaimers">
                        <p className="text-xs leading-relaxed text-zinc-500">
                            <span className="font-semibold text-zinc-400">{t('landing.footer.contentLabel')}</span>{' '}
                            {t('landing.footer.contentText')}
                        </p>
                        <p className="text-xs leading-relaxed text-zinc-500">
                            <span className="font-semibold text-zinc-400">{t('landing.footer.riskLabel')}</span>{' '}
                            {t('landing.footer.riskText')}
                        </p>
                    </div>
                    <ComplianceLinks className="mb-5" />
                    <div className="mb-6"><EduDisclaimer /></div>
                    <SocialLinks className="mb-6" />
                    <p className="text-sm text-center text-zinc-600" data-testid="footer-copyright">
                        © {new Date().getFullYear()} NIKAYO PRIVATE TRADE GUIDANCE. {t('landing.footer.copyright')}
                    </p>
                </div>
            </footer>
        </div>
    );
}
